import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, SearchIcon, Switch } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { PeriodDateRangeFields, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery, useAccountingReportFilters } from './shared/useAccountingReportFilters';

type Vessel = { id: number; code: string; name: string };

type Row = {
  category: string;
  byVessel: Record<number, number>;
  total: number;
};

type Response = {
  vessels: Vessel[];
  rows: Row[];
};

export const RunningCostByVessel: React.FC = () => {
  const { colors } = useTheme();
  const f = useAccountingReportFilters();
  const [useLocalCurrency, setUseLocalCurrency] = useState(false);

  const reportUrl =
    f.run
      ? buildReportQuery('/accounting/reports/running-cost-by-vessel', {
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
          companyIds: f.companyIdsParam || undefined,
          useLocalCurrency,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);
  const rows = data?.rows ?? [];

  const columns = useMemo<Column<Row>[]>(() => {
    if (!data) return [{ key: 'category', header: 'Category' }];
    const cols: Column<Row>[] = [{ key: 'category', header: 'Cost category', sortable: true }];
    for (const vessel of data.vessels) {
      cols.push({
        key: `vessel_${vessel.id}` as keyof Row,
        header: vessel.code,
        align: 'right',
        columnGroup: { id: String(vessel.id), header: vessel.name },
        render: (_v, row) => formatReportAmount(row.byVessel[vessel.id] ?? 0),
      });
    }
    cols.push({
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (v: number) => formatReportAmount(v),
    });
    return cols;
  }, [data]);

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <PeriodDateRangeFields
          periodId={f.periodId}
          periodOptions={f.periodOptions}
          onPeriodChange={(value) => {
            f.setPeriodId(value);
            f.setRun(false);
          }}
          dateFrom={f.dateFrom}
          dateTo={f.dateTo}
          onDateFromChange={(value) => {
            f.setPeriodId('');
            f.setDateFrom(value);
            f.setRun(false);
          }}
          onDateToChange={(value) => {
            f.setPeriodId('');
            f.setDateTo(value);
            f.setRun(false);
          }}
        />
        <Switch label="Local currency" checked={useLocalCurrency} onChange={(e) => { setUseLocalCurrency(e.target.checked); f.setRun(false); }} size="sm" />
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5">
          <span className={cn('h-1.5 w-1.5 rounded-full', f.allCompanies ? 'bg-emerald-500' : 'bg-amber-500')} />
          {f.companyScopeLabel}
        </Badge>
        <Button variant="default" onClick={() => f.setRun(true)} disabled={!f.canSearch} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={f.run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Running Cost by Vessel"
      exportFileBase="running-cost-by-vessel"
      exportDateTo={f.dateTo}
      statusBadge={
        f.run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(f.dateFrom)} – {formatDateGlobal(f.dateTo)}
          </Badge>
        ) : undefined
      }
      statusExtra={
        <Badge variant="secondary" className="font-normal">
          {useLocalCurrency ? 'Local currency' : 'USD'}
        </Badge>
      }
      recordCount={rows.length}
      readyMessage="Choose period and click Search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => r.category}
        stickyHeader
        emptyMessage="No vessel costs in period."
      />
    </AccountingReportShell>
  );
};
